 
<template>
  <div class="w-full h-full" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="ml-4 mr-4 mt-4 flex" style="justify-content: space-between; align-items: center;">
      <div>
        <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">Geni – AI Agent Creator</h1>
        <div class="text text-gray-900 dark:text-gray-200">Guided setup with agentic flow</div>
      </div>
      <div class="flex" style="align-items: center;">
        <button class="btn btn-neutral mr-2" @click="resetAll">Reset</button>
        <button class="btn" :disabled="canCreate === false || isSaving === true" @click="createAgent">
          <span v-if="isSaving === true" class="loading loading-spinner loading-sm mr-2"></span>
          {{ createdAgentId === null ? 'Create Agent' : 'Save Changes' }}
        </button>
      </div>
    </div>
    <div class="ml-4 mr-4 mt-4 flex" style="align-items: stretch;">
      <div style="width: 58%; min-height: 70vh;">
        <div class="chat-assistance-container" :class="{ 'rtl': isRtl === true }">
          <div class="chat-header">
            <div class="flex items-center">
              <button @click="startNewChat" class="btn btn-sm btn-outline">
                New Chat
              </button>
            </div>
            <div class="agent-info">
              <span class="agent-name">Geni</span>
              <span class="agent-status">Active</span>
            </div>
          </div>
          <div class="chat-content">
            <div class="chat-messages" ref="chatMessages">
              <template v-for="(message, index) in messages" :key="index">
                <div :class="['message', message.type === 'user' ? 'user-message' : 'agent-message', message.isLoading === true ? 'typing' : '']" :message-id="message.id" :style="{ direction: message.direction, textAlign: message.direction === 'rtl' ? 'right' : 'left' }">
                  <div class="message-wrapper">
                    <div class="message-content" :style="{ direction: message.direction, textAlign: message.direction === 'rtl' ? 'right' : 'left' }">
                      <div v-if="message.isLoading === true">
                        <svg width="60" height="23" viewBox="0 0 120 40" xmlns="http://www.w3.org/2000/svg"><circle cx="20" cy="20" r="8"><animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0s"/></circle><circle cx="60" cy="20" r="8"><animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0.2s"/></circle><circle cx="100" cy="20" r="8"><animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0.4s"/></circle></svg>
                      </div>
                      <div v-else-if="message.type === 'agent'" v-html="sanitizeHtml(message.text)"></div>
                      <div v-else>{{ message.text }}</div>
                      <div v-if="message.type === 'user' && message.attachedFiles !== undefined && message.attachedFiles !== null && message.attachedFiles.length > 0" class="attached-files-display">
                        <div v-for="(file, fIndex) in message.attachedFiles" :key="fIndex" class="attached-file-item">
                          <template v-if="file.dataUrl !== null && file.dataUrl !== undefined">
                            <img :src="file.dataUrl" :alt="file.name" class="attached-file-preview-image">
                          </template>
                          <template v-else>
                            <span class="attached-file-name" :title="file.name">{{ file.name }}</span>
                          </template>
                        </div>
                      </div>
                      <div v-if="message.type === 'agent' && message.extra === 'fieldPrompt'" class="mt-2">
                        <template v-if="message.fieldId === 'name'">
                          <div class="flex" style="align-items: center;">
                            <input type="text" v-model="agentName" class="input input-bordered flex-1 mr-2" placeholder="Agent name" />
                            <button class="btn btn-sm" @click="submitFieldInline('name')">Save</button>
                          </div>
                        </template>
                        <template v-else-if="message.fieldId === 'role'">
                          <div class="suggested-responses">
                            <button v-for="(opt, sIndex) in roleOptions" :key="sIndex" @click="selectRole(opt)" class="suggestion-button btn btn-sm btn-outline">{{ opt }}</button>
                          </div>
                        </template>
                        <template v-else-if="message.fieldId === 'organization'">
                          <div class="flex" style="align-items: center;">
                            <input type="text" v-model="organization" class="input input-bordered flex-1 mr-2" placeholder="Organization" />
                            <button class="btn btn-sm" @click="submitFieldInline('organization')">Save</button>
                          </div>
                        </template>
                        <template v-else-if="message.fieldId === 'type'">
                          <div class="suggested-responses">
                            <button class="suggestion-button btn btn-sm btn-outline" @click="selectType('PublicAgent')">Public</button>
                            <button class="suggestion-button btn btn-sm btn-outline" @click="selectType('PrivateAgent')">Private</button>
                          </div>
                        </template>
                        <template v-else-if="message.fieldId === 'divisions' && agentType === 'PrivateAgent'">
                          <div class="mt-2">
                            <DivisionSelect v-model="selectedDivisionIds" :options="divisions" :error="divisionError" :errorMessage="divisionErrorMessage" />
                            <div class="mt-2">
                              <button class="btn btn-sm" @click="submitFieldInline('divisions')">Done</button>
                            </div>
                          </div>
                        </template>
                        <template v-else-if="message.fieldId === 'tone'">
                          <div class="flex" style="align-items: center;">
                            <input type="text" v-model="tone" class="input input-bordered flex-1 mr-2" placeholder="Tone" />
                            <button class="btn btn-sm" @click="submitFieldInline('tone')">Save</button>
                          </div>
                          <div class="suggested-responses">
                            <button v-for="(opt, sIndex) in toneOptions" :key="sIndex" @click="quickTone(opt)" class="suggestion-button btn btn-sm btn-outline">{{ opt }}</button>
                          </div>
                        </template>
                        <template v-else-if="message.fieldId === 'knowledge'">
                          <div v-if="uploadedFiles.length === 0" class="w-full p-4 border-2 border-dashed rounded-lg cursor-pointer"
                               :class="{ 'border-primary': kbDragging === true, 'border-gray-300': kbDragging === false }"
                               @click="triggerKbFileInput"
                               @dragenter.prevent="kbDragging = true"
                               @dragleave.prevent="kbDragging = false"
                               @dragover.prevent
                               @drop.prevent="handleKbDrop">
                            <div class="flex" style="flex-direction: column; align-items: center;">
                              <svg class="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                              <div class="text-xs text-gray-500">Drag & drop files here or click to select</div>
                            </div>
                            <input ref="kbFileInput" type="file" class="hidden" multiple accept=".docx,.pdf,.txt,.xlsx" @change="handleKbFileInputChange" />
                          </div>
                          <template v-else>
                            <div class="text-xs text-gray-500">Files staged: {{ uploadedFiles.length }}</div>
                            <ul class="mt-2">
                              <li v-for="f in uploadedFiles" :key="f.fileName" class="flex" style="justify-content: space-between; align-items: center; border-bottom: 1px dashed rgba(0,0,0,0.1); padding: 6px 0;">
                                <span :title="f.fileName">{{ truncateDisplayFileName(f.fileName, 60) }}</span>
                                <button class="btn btn-xs" @click="removeUploadedKb(f.fileName)">Remove</button>
                              </li>
                            </ul>
                            <div class="mt-2">
                              <button class="btn btn-sm" @click="submitFieldInline('knowledge')">Continue</button>
                            </div>
                          </template>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="false === true" class="suggested-responses"></div>
              </template>
            </div>
            <div class="chat-input" :class="{ 'is-dragging-over': isDragging === true }" @dragenter.prevent.stop="handleDragEnter" @dragover.prevent.stop="handleDragOver" @dragleave.prevent.stop="handleDragLeave" @drop.prevent.stop="handleDrop">
              <div class="staged-files-container" v-if="stagedFiles.length > 0">
                <div v-for="(fileItem, index) in stagedFiles" :key="index" class="file-card">
                  <template v-if="fileItem.previewUrl !== null">
                    <img :src="fileItem.previewUrl" class="file-preview-image" :alt="fileItem.file.name" />
                  </template>
                  <template v-else>
                    <span class="file-name" :title="fileItem.file.name">{{ fileItem.file.name }}</span>
                  </template>
                  <button @click="removeStagedFile(index)" class="remove-file-button">x</button>
                </div>
              </div>
              <div class="input-row">
                <div class="file-upload">
                  <input type="file" ref="fileInput" @change="handleFileUpload" multiple class="hidden" accept=".pdf,.doc,.docx,.txt,.xlsx" />
                  <button @click="triggerFileInput" class="upload-button" title="Attach files">+</button>
                </div>
                <textarea v-model="userMessage" @keyup.enter.prevent="sendMessage" @input="checkDirection($event.target.value)" :style="{ direction: inputDirection, textAlign: inputDirection === 'rtl' ? 'right' : 'left' }" placeholder="Type your answer" ref="messageInput"></textarea>
                <button @click.prevent="sendMessage" class="send-button" title="Send">Send</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="ml-4 card bg-base-100 border-base-300 rounded-box p-4" style="width: 40%; min-height: 70vh;">
        <div v-if="createdAgentId === null" class="w-full h-full flex" style="align-items: center; justify-content: center; text-align: center;">
          <div>
            <div class="text-lg mb-2">Preview will appear here after creation</div>
            <div class="text-sm text-gray-500">Public widget preview</div>
          </div>
        </div>
        <div v-else style="width: 100%; height: 100%; min-height: 65vh;">
          <iframe :src="previewUrl" style="border: 0; width: 100%; height: 100%; min-height: 65vh;"></iframe>
        </div>
        <div v-if="createdAgentId !== null && agentType === 'PrivateAgent'" class="mt-2 text-xs text-warning">Preview supports public agents</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, nextTick, watch } from 'vue';
import { useRoute } from 'vue-router';
import axios from '../api/axiosInstance';
import { globalState } from '@/globalState';
import DivisionSelect from '@/components/DivisionSelect.vue';
import { useToast } from 'vue-toastification';
import { useI18n } from '@/i18n';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';

const { t, currentLanguage } = useI18n();
const toast = useToast();
const route = useRoute();

const isSaving = ref(false);
const createdAgentId = ref(null);
const geniSessionId = ref(null);
const isDragging = ref(false);
const fileInput = ref(null);
const chatMessages = ref(null);
const messageInput = ref(null);

const roleOptions = ['Sales Agent','Customer Support','Q&A Agent','HR Agent'];
const toneOptions = ['Friendly and helpful','Professional and concise','Casual and engaging','Empathetic and supportive'];

const agentName = ref('');
const role = ref('');
const organization = ref('');
const agentType = ref('PublicAgent');
const selectedDivisionIds = ref([]);
const tone = ref('');
 

const divisions = ref([]);
const divisionError = ref(false);
const divisionErrorMessage = ref('Select at least one division');

const availableLLMModels = ref([]);
const selectedLLMModel = ref('');

const uploadedFiles = ref([]);
 

function truncateDisplayFileName(name, max) {
  if (typeof name !== 'string') return '';
  if (Number.isFinite(max) === false) return name;
  if (name.length > max) return name.slice(0, max - 3) + '...';
  return name;
}

const isRtl = computed(() => currentLanguage.value === 'he' || currentLanguage.value === 'ar');

const messages = ref([]);
const userMessage = ref('');
const inputDirection = ref('ltr');
  const kbDragging = ref(false);
  const kbFileInput = ref(null);
const previewUrl = ref('');

 

const canCreate = computed(() => {
  if (agentName.value.trim().length <= 0) return false;
  return true;
});

function appendMessage(type, text, options = {}) {
  const id = `m-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  const safeText = type === 'agent' && options.isLoading !== true ? sanitizeHtml(text) : text;
  const msg = {
    id: id,
    type: type,
    text: safeText,
    isLoading: options.isLoading === true,
    direction: options.direction !== undefined && options.direction !== null ? options.direction : 'ltr',
    attachedFiles: Array.isArray(options.attachedFiles) === true ? options.attachedFiles : [],
    suggestedResponses: Array.isArray(options.suggestedResponses) === true ? options.suggestedResponses : [],
    extra: options.extra
  };
  messages.value.push(msg);
  nextTick(() => scrollToBottom());
  return id;
}

function updateAgentMessage(id, newHtml, suggestions = [], opts = {}) {
  const idx = messages.value.findIndex(m => m.id === id);
  if (idx > -1) {
    messages.value[idx].text = sanitizeHtml(newHtml);
    messages.value[idx].isLoading = false;
    messages.value[idx].suggestedResponses = Array.isArray(suggestions) === true ? suggestions : [];
    if (opts.extra !== undefined) messages.value[idx].extra = opts.extra;
    nextTick(() => scrollToBottom());
  }
}

function getTextDirection(text) {
  if (typeof text !== 'string' || text.length <= 0) return 'ltr';
  const rtlRegex = /[\u0590-\u05FF\u0600-\u06FF]/;
  return rtlRegex.test(text) === true ? 'rtl' : 'ltr';
}

function checkDirection(text) {
  inputDirection.value = getTextDirection(text);
}

function scrollToBottom() {
  if (chatMessages.value !== null) chatMessages.value.scrollTop = chatMessages.value.scrollHeight;
}

function startNewChat() {
  messages.value = [];
  stagedFiles.value = [];
  
  beginFlow();
  console.log('GENI startNewChat');
}

function beginFlow() {
  appendMessage('agent', 'I will help you create an AI agent. You can answer step by step or provide multiple fields like "name: Acme Bot; role: Sales Agent; organization: Acme; type: Public; tone: Professional" in one message.');
  agentNext();
  console.log('GENI beginFlow');
}

function handleSuggestionClick(suggestion) {
  userMessage.value = suggestion;
  sendMessage();
  console.log('GENI handleSuggestionClick', { suggestion: suggestion });
}

async function sendMessage() {
  if (userMessage.value.trim().length <= 0 && stagedFiles.value.length <= 0) return;
  let text = userMessage.value.trim();
  let filesPayload = [];
  let filesForDisplay = [];
  console.log('GENI sendMessage start', { text: text, stagedFiles: stagedFiles.value.length });
  if (stagedFiles.value.length > 0) {
    const current = [...stagedFiles.value];
    try {
      const readResults = await Promise.all(current.map(fi => readFileAsBase64(fi.file)));
      filesPayload = readResults.map((r, i) => ({ fileName: current[i].file.name, fileType: r.fileType === 'txt' ? 'text' : r.fileType, fileData: r.dataUrl.split(',')[1], status: 'new' }));
      filesForDisplay = readResults.map((r, i) => ({ name: current[i].file.name, type: r.fileType, dataUrl: r.isImage === true ? r.dataUrl : null }));
      for (let i = 0; i < filesPayload.length; i = i + 1) {
        if (uploadedFiles.value.some(x => x.fileName === filesPayload[i].fileName) === false) uploadedFiles.value.push(filesPayload[i]);
      }
      for (let i = 0; i < current.length; i = i + 1) {
        if (current[i].previewUrl !== null) URL.revokeObjectURL(current[i].previewUrl);
      }
      stagedFiles.value = [];
      console.log('GENI sendMessage filesProcessed', { count: filesPayload.length });
    } catch (e) {
      stagedFiles.value = [];
      console.error('GENI sendMessage filesError', e);
    }
  }
  
  if (text.length === 0 && filesPayload.length > 0) {
    const list = filesPayload.map(f => `${f.fileName} (${f.fileType})`).join(', ');
    text = `Knowledge files: ${list}`;
  }
  const dir = getTextDirection(text);
  appendMessage('user', text.length > 0 ? text : '(uploaded files)', { attachedFiles: filesForDisplay, direction: dir });
  if (text.length > 0) parseFreeText(text);
  try {
    if (geniSessionId.value === null) {
      await startGeniSession();
    }
    const headers = getAuthHeaders();
    if (headers === null) return;
    let payload = { sessionId: geniSessionId.value, message: text.length > 0 ? text : 'I uploaded knowledge files.' };
    if (filesPayload.length > 0) {
      payload = { ...payload, filesInfo: filesPayload.map(f => ({ fileName: f.fileName, fileType: f.fileType, size: (f.fileData || '').length })) };
    }
    const res = await axios.post(`${globalState.apiEndpoint}/api/geni/step`, payload, { headers });
    renderAgentTurn(res.data);
    console.log('GENI sendMessage stepResponse', res.data);
  } catch (e) {
    const id = appendMessage('agent', 'Sorry, there was an error processing your message.');
    updateAgentMessage(id, 'Sorry, there was an error processing your message.');
    console.error('GENI sendMessage stepError', e);
  }
  userMessage.value = '';
}

function parseFreeText(text) {
  const nameMatch = text.match(/name\s*:\s*([^,;\n]+)/i);
  if (nameMatch !== null && nameMatch[1] !== undefined) agentName.value = nameMatch[1].trim();
  const roleMatch = text.match(/role\s*:\s*([^,;\n]+)/i);
  if (roleMatch !== null && roleMatch[1] !== undefined) role.value = normalizeRole(roleMatch[1].trim());
  const orgMatch = text.match(/organization\s*:\s*([^,;\n]+)/i);
  if (orgMatch !== null && orgMatch[1] !== undefined) organization.value = orgMatch[1].trim();
  const typeMatch = text.match(/type\s*:\s*(public|private)/i);
  if (typeMatch !== null && typeMatch[1] !== undefined) agentType.value = typeMatch[1].toLowerCase() === 'public' ? 'PublicAgent' : 'PrivateAgent';
  const toneMatch = text.match(/tone\s*:\s*([^\n]+)/i);
  if (toneMatch !== null && toneMatch[1] !== undefined) tone.value = toneMatch[1].trim();
  const divMatch = text.match(/divisions\s*:\s*([0-9,\s]+)/i);
  if (divMatch !== null && divMatch[1] !== undefined) {
    const parts = divMatch[1].split(',').map(p => Number(p.trim())).filter(n => Number.isFinite(n) === true);
    if (parts.length > 0) selectedDivisionIds.value = parts;
  }
  console.log('GENI parseFreeText applied');
  if (createdAgentId.value !== null) handleModificationIntents(text);
}

function normalizeRole(raw) {
  const r = raw.toLowerCase();
  if (r.includes('sales') === true) return 'Sales Agent';
  if (r.includes('support') === true) return 'Customer Support';
  if (r.includes('q&a') === true || r.includes('qa') === true) return 'Q&A Agent';
  if (r.includes('hr') === true || r.includes('human') === true) return 'HR Agent';
  return raw;
}

function agentNext() {}

function handleModificationIntents(text) {
  const lower = text.toLowerCase();
  let changed = false;
  const nameM = text.match(/change\s+name\s+to\s+(.+)/i);
  if (nameM !== null && nameM[1] !== undefined) { agentName.value = nameM[1].trim(); changed = true; }
  const roleM = text.match(/change\s+role\s+to\s+(.+)/i);
  if (roleM !== null && roleM[1] !== undefined) { role.value = normalizeRole(roleM[1].trim()); changed = true; }
  if (lower.includes('make it private') === true) { agentType.value = 'PrivateAgent'; changed = true; }
  if (lower.includes('make it public') === true) { agentType.value = 'PublicAgent'; changed = true; }
  const toneM = text.match(/(change\s+)?tone\s+(to|is)\s+(.+)/i);
  if (toneM !== null && toneM[3] !== undefined) { tone.value = toneM[3].trim(); changed = true; }
  console.log('GENI handleModificationIntents', { changed: changed });
  if (changed === true) { createAgent(); }
}

async function fetchDivisions() {
  try {
    const headers = getAuthHeaders();
    if (headers === null) return;
    const res = await axios.get(`${globalState.apiEndpoint}/api/divisions`, { headers });
    divisions.value = Array.isArray(res.data) === true ? res.data : [];
    console.log('GENI fetchDivisions ok', { count: divisions.value.length });
  } catch (e) {
    divisions.value = [];
    console.error('GENI fetchDivisions error', e);
  }
}

async function fetchLLMModels() {
  try {
    const res = await axios.get(`${globalState.apiEndpoint}/api/config/llmModels`);
    availableLLMModels.value = Array.isArray(res.data) === true ? res.data : [];
    if (availableLLMModels.value.length > 0) selectedLLMModel.value = availableLLMModels.value[0].id;
    console.log('GENI fetchLLMModels ok', { count: availableLLMModels.value.length, selected: selectedLLMModel.value });
  } catch (e) {
    availableLLMModels.value = [];
    console.error('GENI fetchLLMModels error', e);
  }
}

function buildDescription() {
  const roleText = role.value.trim().length > 0 ? role.value : 'Q&A Agent';
  const orgText = organization.value.trim().length > 0 ? organization.value : 'Organization';
  const toneText = tone.value.trim().length > 0 ? tone.value : 'Friendly and helpful';
  const parts = [];
  parts.push(`You are a ${roleText} for ${orgText}.`);
  parts.push(`Use a ${toneText} tone.`);
  parts.push('Answer clearly and helpfully.');
  return parts.join(' ');
}

function buildStartingMessage() {
  const roleText = role.value.trim().length > 0 ? role.value : 'Q&A Agent';
  const orgText = organization.value.trim().length > 0 ? organization.value : 'Organization';
  return `Hello, I am ${agentName.value}, the ${roleText} for ${orgText}. How can I help?`;
}

async function createAgent() {
  if (canCreate.value === false) {
    const id = appendMessage('agent', 'Some required fields are missing. Please continue answering the questions.');
    updateAgentMessage(id, 'Some required fields are missing. Please continue answering the questions.');
    console.warn('GENI createAgent blocked', { name: agentName.value, role: role.value, org: organization.value, type: agentType.value, tone: tone.value, model: selectedLLMModel.value, divisions: selectedDivisionIds.value });
    return;
  }
  isSaving.value = true;
  try {
    if (geniSessionId.value === null) {
      await startGeniSession();
    }
    const headers = getAuthHeaders();
    if (headers === null) { isSaving.value = false; return };
    const roleText = role.value.trim().length > 0 ? role.value : 'Q&A Agent';
    const orgText = organization.value.trim().length > 0 ? organization.value : 'Organization';
    const typeText = agentType.value === 'PrivateAgent' ? 'Private' : 'Public';
    const selectedModel = (selectedLLMModel.value !== undefined && selectedLLMModel.value !== null && String(selectedLLMModel.value).length > 0)
      ? selectedLLMModel.value
      : (availableLLMModels.value.length > 0 ? availableLLMModels.value[0].id : '');
    const effectiveAgentType = agentType.value === 'PrivateAgent' || agentType.value === 'PublicAgent' ? agentType.value : 'PublicAgent';
    const fieldsOverride = {
      aiAgentName: agentName.value,
      agentType: effectiveAgentType,
      agentMode: 'Agentic',
      aiAgentStatus: 'active',
      divisionIds: effectiveAgentType === 'PrivateAgent' ? selectedDivisionIds.value : [],
      agentShortDescription: `${roleText} for ${orgText} (${typeText})`,
      agentData: {
        description: buildDescription(),
        startingMessage: { default: buildStartingMessage() },
        selectedLlmModelId: selectedModel,
        files: uploadedFiles.value.map(f => ({ fileName: f.fileName, fileType: f.fileType, fileData: f.fileData, status: 'new' }))
      }
    };
    console.log('GENI createAgent payload', { modifyId: createdAgentId.value, fieldsOverride: fieldsOverride });
    const res = await axios.post(`${globalState.apiEndpoint}/api/geni/commit`, { sessionId: geniSessionId.value, modifyAgentId: createdAgentId.value, fieldsOverride }, { headers, timeout: 0 });
    const newId = res.data.agentId;
    createdAgentId.value = String(newId);
    toast.success('Saved');
    mountPublicPreview();
    const id = appendMessage('agent', 'Agent saved. You can continue to adjust settings by telling me changes.');
    updateAgentMessage(id, 'Agent saved. You can continue to adjust settings by telling me changes.', ['Change Name','Change Role','Change Tone','Make Private','Make Public','Add Knowledge']);
    console.log('GENI createAgent result', res.data);
  } catch (e) {
    toast.error('Failed to save agent');
    console.error('GENI createAgent error', e);
  } finally {
    isSaving.value = false;
  }
}

function mountPublicPreview() {
  if (createdAgentId.value === null) return;
  previewUrl.value = `${window.location.origin}/agent/${createdAgentId.value}`;
  console.log('GENI mountPublicPreview', { agentId: createdAgentId.value });
}

function resetAll() {
  agentName.value = '';
  role.value = '';
  organization.value = '';
  agentType.value = 'PublicAgent';
  selectedDivisionIds.value = [];
  tone.value = '';
  uploadedFiles.value = [];
  messages.value = [];
  createdAgentId.value = null;
  beginFlow();
  console.log('GENI resetAll');
}

function getAuthHeaders() {
  const tokenPair = document.cookie.split('; ').find(r => r.startsWith('authToken=') === true);
  const token = tokenPair !== undefined ? tokenPair.split('=')[1] : '';
  const headers = { 'Content-Type': 'application/json' };
  if (token !== '' && token !== undefined && token !== null) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

const stagedFiles = ref([]);

function triggerFileInput() {
  if (fileInput.value !== null) fileInput.value.click();
  console.log('GENI triggerFileInput');
}

function triggerKbFileInput() {
  const el = Array.isArray(kbFileInput.value) === true ? kbFileInput.value[0] : kbFileInput.value;
  if (el !== null && el !== undefined && typeof el.click === 'function') { el.click(); }
  console.log('GENI triggerKbFileInput', { isArray: Array.isArray(kbFileInput.value) === true, count: Array.isArray(kbFileInput.value) === true ? kbFileInput.value.length : (kbFileInput.value !== null && kbFileInput.value !== undefined ? 1 : 0) });
}

function handleDragEnter() { isDragging.value = true; }
function handleDragOver() {}
function handleDragLeave(event) {
  if (event.currentTarget.contains(event.relatedTarget) === true) return;
  isDragging.value = false;
  console.log('GENI handleDragLeave');
}
function handleDrop(event) {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files === null || files === undefined || files.length <= 0) return;
  processFiles(files)
  console.log('GENI handleDrop', { count: files !== undefined && files !== null ? files.length : 0 });
}
function handleFileUpload(event) {
  const files = event.target.files;
  if (files === null || files === undefined || files.length <= 0) return;
  processFiles(files);
  event.target.value = '';
  console.log('GENI handleFileUpload', { count: files !== undefined && files !== null ? files.length : 0 });
}
function processFiles(files) {
  const newFiles = Array.from(files).filter(f => f.type !== '').map(file => {
    let previewUrl = null;
    if (file.type.startsWith('image/') === true) previewUrl = URL.createObjectURL(file);
    return { file, previewUrl };
  })
  const uniques = newFiles.filter(nf => stagedFiles.value.some(ex => ex.file.name === nf.file.name && ex.file.size === nf.file.size) === false);
  stagedFiles.value = [...stagedFiles.value, ...uniques];
  console.log('GENI processFiles', { added: uniques.length });
}

function handleKbDrop(event) {
  kbDragging.value = false;
  const files = event.dataTransfer !== null && event.dataTransfer !== undefined ? event.dataTransfer.files : null;
  if (files === null || files === undefined || files.length <= 0) return;
  addFilesImmediate(Array.from(files));
  console.log('GENI handleKbDrop', { count: files !== null && files !== undefined ? files.length : 0 });
}

function handleKbFileInputChange(event) {
  const files = event.target.files;
  if (files === null || files === undefined || files.length <= 0) return;
  addFilesImmediate(Array.from(files));
  event.target.value = '';
  console.log('GENI handleKbFileInputChange', { count: files !== undefined && files !== null ? files.length : 0 });
}

async function addFilesImmediate(files) {
  const allowed = ['pdf','docx','txt','xlsx'];
  for (let i = 0; i < files.length; i = i + 1) {
    const f = files[i];
    const ext = f.name.split('.').pop().toLowerCase();
    if (allowed.includes(ext) === false) { continue; }
    try {
      const r = await readFileAsBase64(f);
      const base64 = String(r.dataUrl).split(',')[1];
      const entry = { fileName: f.name, fileType: r.fileType === 'txt' ? 'text' : r.fileType, fileData: base64, status: 'new' };
      if (uploadedFiles.value.some(x => x.fileName === entry.fileName) === false) {
        uploadedFiles.value.push(entry);
      }
    } catch (e) {}
  }
  console.log('GENI addFilesImmediate', { total: uploadedFiles.value.length });
}

function removeUploadedKb(name) {
  const idx = uploadedFiles.value.findIndex(x => x.fileName === name);
  if (idx >= 0) {
    uploadedFiles.value.splice(idx, 1);
  }
  console.log('GENI removeUploadedKb', { name: name, total: uploadedFiles.value.length });
}
function removeStagedFile(index) {
  const fileItem = stagedFiles.value[index];
  if (fileItem !== undefined && fileItem.previewUrl !== null) URL.revokeObjectURL(fileItem.previewUrl);
  stagedFiles.value.splice(index, 1)
  console.log('GENI removeStagedFile', { index: index, total: stagedFiles.value.length });
}
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const fileType = file.name.split('.').pop()?.toLowerCase();
      const isImage = file.type.startsWith('image/') === true;
      resolve({ dataUrl, fileType, isImage });
    };
    reader.onerror = err => reject(err);
    reader.readAsDataURL(file);
  });
}

async function startGeniSession() {
  try {
    const headers = getAuthHeaders();
    if (headers === null) return;
    console.log('GENI startGeniSession request');
    const res = await axios.post(`${globalState.apiEndpoint}/api/geni/start`, {}, { headers });
    geniSessionId.value = res.data.sessionId;
    const id = appendMessage('agent', res.data.assistantMessage !== undefined && res.data.assistantMessage !== null ? res.data.assistantMessage : 'Hi, let’s get started.');
    updateAgentMessage(id, res.data.assistantMessage !== undefined && res.data.assistantMessage !== null ? res.data.assistantMessage : 'Hi, let’s get started.', Array.isArray(res.data.suggestions) ? res.data.suggestions : []);
    console.log('GENI startGeniSession response', res.data);
  } catch (e) {
    const id = appendMessage('agent', 'Service is unavailable. Please try again later.');
    updateAgentMessage(id, 'Service is unavailable. Please try again later.');
    console.error('GENI startGeniSession error', e);
  }
}

function renderAgentTurn(stepData) {
  try {
    const f = stepData.fields !== undefined && stepData.fields !== null ? stepData.fields : {};
    if (typeof f.aiAgentName === 'string') agentName.value = f.aiAgentName;
    if (typeof f.agentType === 'string') agentType.value = f.agentType;
    if (typeof f.agentShortDescription === 'string') {}
    if (Array.isArray(f.divisionIds) === true) selectedDivisionIds.value = f.divisionIds;
    if (typeof f.agentData?.description === 'string') {
      tone.value = tone.value;
    }
    let extra = undefined;
    let fieldId = undefined;
    if (typeof stepData.fieldId === 'string' && stepData.fieldId.length > 0) {
      extra = 'fieldPrompt';
      fieldId = stepData.fieldId;
    }
    const id = appendMessage('agent', stepData.assistantMessage !== undefined && stepData.assistantMessage !== null ? stepData.assistantMessage : 'OK', { extra: extra });
    if (extra === 'fieldPrompt') {
      messages.value[messages.value.length - 1].fieldId = fieldId;
    }
    updateAgentMessage(id, stepData.assistantMessage !== undefined && stepData.assistantMessage !== null ? stepData.assistantMessage : 'OK', Array.isArray(stepData.suggestions) ? stepData.suggestions : [], { extra: extra });
    if (stepData.createNow === true) {
      createAgent();
    }
    console.log('GENI renderAgentTurn', { fieldId: stepData.fieldId, createNow: stepData.createNow === true, suggestions: Array.isArray(stepData.suggestions) ? stepData.suggestions.length : 0 });
  } catch (e) {}
}

onMounted(async () => {
  await fetchLLMModels();
  await fetchDivisions();
  await startGeniSession();
  const qAid = route.query.agentId !== undefined && route.query.agentId !== null ? route.query.agentId : (route.query.agentid !== undefined && route.query.agentid !== null ? route.query.agentid : undefined);
  if (qAid !== undefined && String(qAid).length > 0) {
    createdAgentId.value = String(qAid);
    mountPublicPreview();
  }
})

watch(agentType, (nv) => {
  if (nv === 'PrivateAgent' && selectedDivisionIds.value.length <= 0) {
    const id = appendMessage('agent', 'Select divisions for this private agent', { extra: 'divisions' });
    updateAgentMessage(id, 'Select divisions for this private agent', ['Done'], { extra: 'divisions' });
  }
})

function postStep(messageText) {
  return new Promise(async (resolve, reject) => {
    try {
      if (geniSessionId.value === null) {
        await startGeniSession()
      }
      const headers = getAuthHeaders()
      if (headers === null) { reject(new Error('No auth')); return; }
      console.log('GENI postStep request', { message: messageText });
      const res = await axios.post(`${globalState.apiEndpoint}/api/geni/step`, { sessionId: geniSessionId.value, message: messageText }, { headers });
      renderAgentTurn(res.data);
      console.log('GENI postStep response', res.data);
      resolve(res.data);
    } catch (err) {
      console.error('GENI postStep error', err);
      reject(err);
    }
  })
}

function selectRole(opt) {
  role.value = opt;
  const dir = getTextDirection(opt);
  const text = `Role: ${opt}`;
  appendMessage('user', text, { direction: dir });
  postStep(text);
  console.log('GENI selectRole', { role: opt });
}

function selectType(type) {
  agentType.value = type
  const text = type === 'PrivateAgent' ? 'Type: Private' : 'Type: Public'
  const dir = getTextDirection(text)
  appendMessage('user', text, { direction: dir });
  postStep(text);
  console.log('GENI selectType', { type: type });
}

function quickTone(opt) {
  tone.value = opt;
  const dir = getTextDirection(opt);
  const text = `Tone: ${opt}`;
  appendMessage('user', text, { direction: dir });
  postStep(text);
  console.log('GENI quickTone', { tone: opt });
}

function submitFieldInline(fieldId) {
  if (fieldId === 'name') {
    const v = agentName.value.trim();
    if (v.length > 0) {
      const dir = getTextDirection(v);
      const text = `Name: ${v}`;
      appendMessage('user', text, { direction: dir });
      postStep(text);
    }
    console.log('GENI submitFieldInline', { fieldId: fieldId, value: agentName.value });
    return;
  }
  if (fieldId === 'organization') {
    const v = organization.value.trim();
    if (v.length > 0) {
      const dir = getTextDirection(v);
      const text = `Organization: ${v}`;
      appendMessage('user', text, { direction: dir });
      postStep(text);
    }
    console.log('GENI submitFieldInline', { fieldId: fieldId, value: organization.value });
    return;
  }
  if (fieldId === 'divisions') {
    if (selectedDivisionIds.value.length > 0) {
      const text = `Divisions: ${selectedDivisionIds.value.join(',')}`
      const dir = 'ltr';
      appendMessage('user', text, { direction: dir });
      postStep(text);
    }
    console.log('GENI submitFieldInline', { fieldId: fieldId, value: selectedDivisionIds.value });
    return;
  }
  if (fieldId === 'tone') {
    const v = tone.value.trim();
    if (v.length > 0) {
      const dir = getTextDirection(v);
      const text = `Tone: ${v}`;
      appendMessage('user', text, { direction: dir });
      postStep(text);
    }
    console.log('GENI submitFieldInline', { fieldId: fieldId, value: tone.value });
    return;
  }
  if (fieldId === 'knowledge') {
    if (uploadedFiles.value.length > 0) {
      const list = uploadedFiles.value.map(f => `${f.fileName} (${f.fileType})`).join(', ');
      const text = `Knowledge files: ${list}`;
      const dir = 'ltr';
      appendMessage('user', text, { direction: dir });
      postStep(text);
    }
    console.log('GENI submitFieldInline', { fieldId: fieldId, files: uploadedFiles.value.length });
    return;
  }
}
</script>

<style scoped>
.chat-assistance-container { height: 70vh; overflow: hidden; display: flex; flex-direction: column; background: #f8fafc; width: 100%; }
.chat-assistance-container.rtl { direction: rtl }
.chat-header { padding: 1rem; background: white; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; direction: ltr }
.agent-info { display: flex; align-items: center }
.agent-info > * + * { margin-left: 1rem }
.agent-name { font-weight: 700; color: #1e293b; font-size: 1.1rem }
.agent-status { padding: 0.2rem 0.6rem; background: #dcfce7; color: #166534; border-radius: 9999px; font-size: 0.8rem; font-weight: 500 }
.chat-content { flex: 1; display: flex; flex-direction: column; overflow: hidden; background: white; position: relative; width: 100%; direction: ltr }
.chat-messages { flex: 1; overflow-y: auto; padding: 0.5rem; padding-bottom: 8rem; display: flex; flex-direction: column; max-height: calc(100% - 8rem); overflow-x: hidden; direction: ltr }
.chat-messages > * + * { margin-top: 1rem }
.message { max-width: 80%; padding: 0.75rem 1rem; border-radius: 1rem; line-height: 1.5; opacity: 0; transform: translateY(20px); animation: messageAppear 0.3s ease forwards }
@keyframes messageAppear { to { opacity: 1; transform: translateY(0) } }
.user-message { align-self: flex-end; background: #2563eb; color: white; border-bottom-right-radius: 0.25rem }
.agent-message { align-self: flex-start; background: #f1f5f9; color: #1e293b; border-bottom-left-radius: 0.25rem }
.chat-input { position: absolute; bottom: 1rem; left: 50%; transform: translateX(-50%); width: calc(100% - 2rem); max-width: 800px; background: white; border-radius: 1rem; box-shadow: 0 0 6px -1px rgb(0 0 0 / 29%), 0 2px 4px -1px rgba(0,0,0,0.06); z-index: 10; display: flex; flex-direction: column; padding: 0.5rem; border: 2px dashed transparent; transition: border-color 0.2s ease-in-out, background-color 0.2s ease-in-out }
.is-dragging-over { border-color: #2563eb; background-color: #eff6ff }
.staged-files-container { display: flex; flex-wrap: wrap; margin-top: -0.5rem; margin-left: -0.5rem; padding: 0.2rem 0.5rem 0.6rem; border-bottom: 1px solid #e2e8f0; margin-bottom: 0.5rem }
.staged-files-container > * { margin-top: 0.5rem; margin-left: 0.5rem }
.file-card { display: flex; align-items: center; background-color: #eef2ff; color: #4338ca; padding: 0.25rem 0.5rem; border-radius: 0.75rem; font-size: 0.8rem; max-width: 180px; border: 1px solid #c7d2fe; overflow: hidden }
.file-preview-image { width: 24px; height: 24px; object-fit: cover; margin-right: 0.4rem; border-radius: 4px; flex-shrink: 0 }
.file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex-grow: 1 }
.remove-file-button { background: none; border: none; color: #6366f1; cursor: pointer; padding: 0 0 0 0.3rem; margin-left: 0.2rem; display: flex; align-items: center; flex-shrink: 0 }
.input-row { display: flex; align-items: flex-start }
.input-row > * + * { margin-left: 0.5rem }
.upload-button { padding: 0.65rem; background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; border-radius: 0.5rem; cursor: pointer; display: flex; align-items: center; justify-content: center; height: 4.2rem }
.upload-button:hover { background: #e2e8f0; color: #475569 }
textarea { flex: 1; padding: 0.75rem; border: 1px solid #e2e8f0; border-radius: 0.5rem; resize: none; min-height: 42px; max-height: 120px; font-family: inherit; font-size: 0.875rem; line-height: 1.5 }
.send-button { padding: 0.65rem; background: #2563eb; color: white; border: none; border-radius: 0.5rem; cursor: pointer; display: flex; align-items: center; justify-content: center; height: 42px }
.send-button:hover { background: #1d4ed8 }
.message-content { white-space: wrap; word-wrap: break-word }
.suggested-responses { margin-top: 0.5rem; display: flex; flex-wrap: wrap }
.suggested-responses > * { margin-right: 0.5rem; margin-top: 0.5rem }
</style>

